import type { RingMetric } from "../../components/screen0-ring/Screen0Ring";

export type FocusableTopApp = {
  appIdentifier?: string;
  displayName: string;
  durationSeconds: number;
  category?: string;
  isEstimated?: boolean;
  color?: string;
  opens?: number;
};

export function topAppKey(app: Pick<FocusableTopApp, "appIdentifier" | "displayName">): string {
  return app.appIdentifier ?? app.displayName;
}

export function sortTopAppsForMetric(apps: FocusableTopApp[], metric: RingMetric): FocusableTopApp[] {
  return [...apps].sort((a, b) =>
    metric === "opens"
      ? (b.opens ?? 0) - (a.opens ?? 0) || b.durationSeconds - a.durationSeconds
      : b.durationSeconds - a.durationSeconds,
  );
}

export function resolveFocusedApp(
  topApps: FocusableTopApp[],
  focusedAppKey: string | null,
): FocusableTopApp | null {
  if (!focusedAppKey) return null;
  return topApps.find((app) => topAppKey(app) === focusedAppKey) ?? null;
}

export function resolveFocusedEntity<T extends { entityKey: string }>(
  entities: T[],
  focusedEntityKey: string | null,
): T | null {
  if (!focusedEntityKey) return null;
  return entities.find((entity) => entity.entityKey === focusedEntityKey) ?? null;
}

/** Ring segment index for the focused app, or null when it is outside the top-N ring slice. */
export function resolveRingSegmentIndex(
  topApps: FocusableTopApp[],
  focusedAppKey: string | null,
  ringSize = 5,
): number | null {
  if (!focusedAppKey) return null;
  const index = topApps.slice(0, ringSize).findIndex((app) => topAppKey(app) === focusedAppKey);
  return index >= 0 ? index : null;
}

export function resolveRingSegmentIndexForEntityKey(
  entities: Array<{ entityKey: string }>,
  focusedEntityKey: string | null,
  ringSize = 5,
): number | null {
  if (!focusedEntityKey) return null;
  const index = entities.slice(0, ringSize).findIndex((entity) => entity.entityKey === focusedEntityKey);
  return index >= 0 ? index : null;
}
